// packages/frontend/src/utils/logger.ts
// Single frontend logging path. App code logs through this module, never raw console.
//
// Env:
//   VITE_ENABLE_CONSOLE_LOGGING=true  -> honor VITE_LOG_LEVEL (debug|info|warn|error)
//   VITE_ENABLE_CONSOLE_LOGGING=false -> floor at warn (staging); production sets
//                                        VITE_LOG_LEVEL=error so only errors emit.

type LevelName = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'

const LOG_LEVELS: Record<LevelName, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
}

function parseLevelName(raw: string | undefined): LevelName | undefined {
  if (!raw) return undefined
  const upper = raw.toUpperCase()
  if (upper === 'DEBUG' || upper === 'INFO' || upper === 'WARN' || upper === 'ERROR') {
    return upper
  }
  return undefined
}

export function resolveLogLevel(
  enableConsole: string | undefined,
  logLevel: string | undefined,
  isDev: boolean
): number {
  const named = parseLevelName(logLevel)
  const namedValue = named ? LOG_LEVELS[named] : undefined
  if (enableConsole !== 'true') {
    const floor = LOG_LEVELS.WARN
    return namedValue !== undefined ? Math.max(namedValue, floor) : floor
  }
  if (namedValue !== undefined) return namedValue
  return isDev ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO
}

function currentLogLevel(): number {
  return resolveLogLevel(
    import.meta.env.VITE_ENABLE_CONSOLE_LOGGING,
    import.meta.env.VITE_LOG_LEVEL,
    Boolean(import.meta.env.DEV)
  )
}

class Logger {
  private context: string

  constructor(context: string = 'Frontend') {
    this.context = context
  }

  private shouldLog(level: number): boolean {
    return level >= currentLogLevel()
  }

  private formatMessage(level: string, message: string, data?: unknown): string {
    const timestamp = new Date().toISOString()
    const contextStr = `[${this.context}]`
    const levelStr = `[${level}]`

    if (data !== undefined) {
      return `${timestamp} ${contextStr} ${levelStr} ${message} ${JSON.stringify(data, (_key: string, value: unknown): unknown =>
        value instanceof Error ? { name: value.name, message: value.message } : value
      )}`
    }

    return `${timestamp} ${contextStr} ${levelStr} ${message}`
  }

  debug(message: string, data?: unknown): void {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.debug(this.formatMessage('DEBUG', message, data))
    }
  }

  info(message: string, data?: unknown): void {
    if (this.shouldLog(LOG_LEVELS.INFO)) {
      console.info(this.formatMessage('INFO', message, data))
    }
  }

  warn(message: string, data?: unknown): void {
    if (this.shouldLog(LOG_LEVELS.WARN)) {
      console.warn(this.formatMessage('WARN', message, data))
    }
  }

  error(message: string, data?: unknown): void {
    if (this.shouldLog(LOG_LEVELS.ERROR)) {
      console.error(this.formatMessage('ERROR', message, data))
    }
  }
}

export const logger = new Logger()

export const createLogger = (context: string): Logger => new Logger(context)

export { Logger }

// Factory, not a React hook. Do not put this in useEffect dependency arrays.
export const useLogger = (context?: string): Logger => {
  return context ? new Logger(context) : logger
}
